import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AdvertiseLayout } from '@/components/advertise/AdvertiseLayout';
import { AdvertiseSideSummary } from '@/components/advertise/AdvertiseSideSummary';
import { AdvertiseFooterNav } from '@/components/advertise/AdvertiseFooterNav';
import { useAdvertise } from '@/store/useAdvertise';
import { useStepGuard, buildersStepValid } from '@/utils/advertiseGuards';
import { BUILDERS_STEPS } from '@/config/advertiseSteps';

const HOME_TYPES = ['Single-family', 'Condo', 'Townhome', 'Co-op', 'Manufactured'];

export default function BuildersPortfolio() {
  const navigate = useNavigate();
  const builders = useAdvertise((s) => s.builders);
  const setField = useAdvertise((s) => s.setField);
  useStepGuard(buildersStepValid.portfolio(builders), '/advertise/builders');
  const [marketInput, setMarketInput] = useState('');

  const set = (patch) => setField('builders', patch);
  const addMarket = () => {
    const v = marketInput.trim();
    if (v && !builders.marketsServed.includes(v)) set({ marketsServed: [...builders.marketsServed, v] });
    setMarketInput('');
  };
  const removeMarket = (m) => set({ marketsServed: builders.marketsServed.filter((x) => x !== m) });
  const toggleType = (t) => {
    const has = builders.homeTypes.includes(t);
    set({ homeTypes: has ? builders.homeTypes.filter((x) => x !== t) : [...builders.homeTypes, t] });
  };

  return (
    <AdvertiseLayout
      trackLabel="Community Boost"
      steps={BUILDERS_STEPS}
      currentStepId="portfolio"
      title="What do you build?"
      sidebar={<AdvertiseSideSummary items={[{ label: 'Markets', value: builders.marketsServed.join(', ') }, { label: 'Home types', value: builders.homeTypes.join(', ') }]} />}
      footer={<AdvertiseFooterNav onBack={() => navigate('/advertise/builders/company')} onNext={() => navigate('/advertise/builders/contact')} />}
    >
      <div className="space-y-6">
        <div>
          <Label>Markets you serve</Label>
          <div className="mt-1.5 flex gap-2">
            <Input value={marketInput} onChange={(e) => setMarketInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addMarket(); } }} placeholder="e.g. Tampa, FL" className="rounded-xl" data-testid="builders-portfolio-market-input" />
          </div>
          {builders.marketsServed.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {builders.marketsServed.map((m) => (
                <span key={m} className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                  {m}<button type="button" onClick={() => removeMarket(m)} aria-label={`Remove ${m}`}><X size={12} /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div>
          <Label>Home types</Label>
          <div className="mt-1.5 grid gap-2 sm:grid-cols-2">
            {HOME_TYPES.map((t) => (
              <label key={t} className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5">
                <Checkbox checked={builders.homeTypes.includes(t)} onCheckedChange={() => toggleType(t)} data-testid={`builders-portfolio-type-${t.toLowerCase().replace(/[^a-z]+/g, '-')}`} />
                <span className="text-sm text-slate-700">{t}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="max-w-xs">
          <Label>Annual new-construction volume</Label>
          <Select value={builders.annualVolume} onValueChange={(v) => set({ annualVolume: v })}>
            <SelectTrigger className="mt-1.5 rounded-xl" data-testid="builders-portfolio-annual-volume"><SelectValue placeholder="Select a range" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1-25">1–25 homes/yr</SelectItem>
              <SelectItem value="26-100">26–100 homes/yr</SelectItem>
              <SelectItem value="100+">100+ homes/yr</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </AdvertiseLayout>
  );
}
